import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { format } from 'date-fns';
import { Play, Pause, LogOut, LogIn, Calendar, QrCode, AlertTriangle, Users, BarChart3, Trophy, CheckCircle, X } from 'lucide-react';

interface Winner {
  id: string;
  customerName: string;
  prizeRank: number;
  selectedAt: string;
  storeName: string;
}

interface Participation {
  id: string;
  subDescription: string | null;
  isApproved: boolean;
  isOptedOut: boolean;
  run: {
    id: string;
    instanceNumber: number;
    status: string;
    startDate: string;
    endDate: string;
    numberOfWinners: number | null;
    campaign: {
      id: string;
      name: string;
      description: string | null;
      numberOfWinners: number;
    };
  };
  stats: {
    totalScans: number;
    uniqueCustomers: number;
  };
  winners: Winner[];
  winnersCount: number;
}

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [participations, setParticipations] = useState<Participation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOptOutWarning, setShowOptOutWarning] = useState(false);
  const [pendingOptOut, setPendingOptOut] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showWinnersModal, setShowWinnersModal] = useState<Participation | null>(null);

  useEffect(() => {
    loadParticipations();
  }, []);

  const loadParticipations = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: Participation[] }>('/store/campaigns');
      setParticipations(response.data.data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptOutClick = (runId: string) => {
    setPendingOptOut(runId);
    setShowOptOutWarning(true);
  };

  const confirmOptOut = async () => {
    if (!pendingOptOut) return;

    setActionLoading(pendingOptOut);
    try {
      await apiClient.post(`/store/campaigns/${pendingOptOut}/opt-out`);
      setParticipations(participations.map(p =>
        p.run.id === pendingOptOut ? { ...p, isOptedOut: true } : p
      ));
    } catch (error) {
      console.error('Failed to opt out:', error);
    } finally {
      setActionLoading(null);
      setShowOptOutWarning(false);
      setPendingOptOut(null);
    }
  };

  const handleOptIn = async (runId: string) => {
    setActionLoading(runId);
    try {
      await apiClient.post(`/store/campaigns/${runId}/opt-in`);
      setParticipations(participations.map(p =>
        p.run.id === runId ? { ...p, isOptedOut: false } : p
      ));
    } catch (error) {
      console.error('Failed to opt in:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusDisplay = (participation: Participation) => {
    if (participation.isOptedOut) {
      return {
        icon: LogOut,
        bgColor: 'bg-gray-100',
        iconColor: 'text-gray-500',
        label: 'Opted Out',
        textColor: 'text-gray-500',
      };
    }

    switch (participation.run.status) {
      case 'ACTIVE':
        return {
          icon: Play,
          bgColor: 'bg-green-100',
          iconColor: 'text-green-600',
          label: 'Active',
          textColor: 'text-green-700',
          filled: true,
        };
      case 'PAUSED':
        return {
          icon: Pause,
          bgColor: 'bg-orange-100',
          iconColor: 'text-orange-600',
          label: 'Paused',
          textColor: 'text-orange-700',
          filled: true,
        };
      case 'COMPLETED':
        // Check if winners have been drawn
        if (participation.winnersCount > 0) {
          return {
            icon: Trophy,
            bgColor: 'bg-amber-100',
            iconColor: 'text-amber-600',
            label: 'Winners Drawn',
            textColor: 'text-amber-700',
            filled: true,
          };
        }
        return {
          icon: CheckCircle,
          bgColor: 'bg-blue-100',
          iconColor: 'text-blue-600',
          label: 'Ended',
          textColor: 'text-blue-700',
          filled: false,
        };
      case 'DRAFT':
      default:
        return {
          icon: Play,
          bgColor: 'bg-gray-100',
          iconColor: 'text-gray-400',
          label: 'Draft',
          textColor: 'text-gray-500',
          filled: false,
        };
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  const activeParticipations = participations.filter(p => !p.isOptedOut && p.run.status === 'ACTIVE');
  const otherParticipations = participations.filter(p => p.isOptedOut || p.run.status !== 'ACTIVE');

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-8">My Campaigns</h1>

      {participations.length === 0 ? (
        <div className="card">
          <div className="text-center py-12 text-gray-500">
            You're not participating in any campaigns yet. Check back soon!
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active Campaigns */}
          {activeParticipations.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-700 mb-3">Active Campaigns</h2>
              <div className="space-y-4">
                {activeParticipations.map((participation) => {
                  const status = getStatusDisplay(participation);
                  const Icon = status.icon;
                  return (
                    <div key={participation.id} className="card">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${status.bgColor}`}>
                            <Icon
                              className={`w-5 h-5 ${status.iconColor}`}
                              fill={status.filled ? 'currentColor' : 'none'}
                            />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {participation.run.campaign.name}
                            </h3>
                            <p className="text-sm text-gray-500">
                              Occurrence #{participation.run.instanceNumber}
                            </p>
                            {participation.run.campaign.description && (
                              <p className="text-sm text-gray-600 mt-1">
                                {participation.run.campaign.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${status.bgColor} ${status.textColor}`}>
                            {status.label}
                          </span>
                        </div>
                      </div>

                      {/* Stats Row */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-primary-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-primary-600 mb-1">
                            <BarChart3 className="w-4 h-4" />
                            <span className="text-xs font-medium">Total Scans</span>
                          </div>
                          <p className="text-2xl font-bold text-primary-900">{participation.stats.totalScans}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-green-600 mb-1">
                            <Users className="w-4 h-4" />
                            <span className="text-xs font-medium">Unique Customers</span>
                          </div>
                          <p className="text-2xl font-bold text-green-900">{participation.stats.uniqueCustomers}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-600 mb-1">
                            <Calendar className="w-4 h-4" />
                            <span className="text-xs font-medium">Duration</span>
                          </div>
                          <p className="text-sm font-medium text-gray-900">
                            {format(new Date(participation.run.startDate), 'MMM d')} - {format(new Date(participation.run.endDate), 'MMM d')}
                          </p>
                        </div>
                        <button
                          onClick={() => participation.winnersCount > 0 && setShowWinnersModal(participation)}
                          disabled={participation.winnersCount === 0}
                          className={`bg-purple-50 rounded-lg p-3 text-left w-full transition-colors ${
                            participation.winnersCount > 0 ? 'hover:bg-purple-100 cursor-pointer' : 'cursor-default'
                          }`}
                        >
                          <div className="flex items-center gap-2 text-purple-600 mb-1">
                            <Trophy className="w-4 h-4" />
                            <span className="text-xs font-medium">Winners</span>
                          </div>
                          {participation.winnersCount > 0 ? (
                            <p className="text-2xl font-bold text-purple-900">
                              {participation.winnersCount} <span className="text-sm font-normal">drawn</span>
                            </p>
                          ) : (
                            <p className="text-sm text-purple-700">
                              {participation.run.numberOfWinners || participation.run.campaign.numberOfWinners} to be drawn
                            </p>
                          )}
                        </button>
                      </div>

                      <div className="mt-4 pt-4 border-t flex items-center justify-end">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/store/qrcodes`)}
                            className="btn-secondary text-sm"
                          >
                            View QR Code
                          </button>
                          <button
                            onClick={() => handleOptOutClick(participation.run.id)}
                            disabled={actionLoading === participation.run.id}
                            className="text-red-600 hover:text-red-700 text-sm px-3 py-2 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            {actionLoading === participation.run.id ? 'Processing...' : 'Opt Out'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Other Campaigns (Draft, Paused, Opted Out) */}
          {otherParticipations.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-700 mb-3">Other Campaigns</h2>
              <div className="space-y-4">
                {otherParticipations.map((participation) => {
                  const status = getStatusDisplay(participation);
                  const Icon = status.icon;
                  return (
                    <div key={participation.id} className="card opacity-75">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${status.bgColor}`}>
                            <Icon
                              className={`w-5 h-5 ${status.iconColor}`}
                              fill={status.filled ? 'currentColor' : 'none'}
                            />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {participation.run.campaign.name}
                            </h3>
                            <p className="text-sm text-gray-500">
                              Occurrence #{participation.run.instanceNumber}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${status.bgColor} ${status.textColor}`}>
                            {status.label}
                          </span>
                          {participation.isOptedOut && participation.run.status === 'ACTIVE' && (
                            <button
                              onClick={() => handleOptIn(participation.run.id)}
                              disabled={actionLoading === participation.run.id}
                              className="flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm px-3 py-2 hover:bg-primary-50 rounded-lg transition-colors"
                            >
                              <LogIn className="w-4 h-4" />
                              {actionLoading === participation.run.id ? 'Processing...' : 'Opt Back In'}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Stats Row */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-100 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-500 mb-1">
                            <BarChart3 className="w-4 h-4" />
                            <span className="text-xs font-medium">Total Scans</span>
                          </div>
                          <p className="text-2xl font-bold text-gray-700">{participation.stats.totalScans}</p>
                        </div>
                        <div className="bg-gray-100 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-500 mb-1">
                            <Users className="w-4 h-4" />
                            <span className="text-xs font-medium">Unique Customers</span>
                          </div>
                          <p className="text-2xl font-bold text-gray-700">{participation.stats.uniqueCustomers}</p>
                        </div>
                        <div className="bg-gray-100 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-500 mb-1">
                            <Calendar className="w-4 h-4" />
                            <span className="text-xs font-medium">Duration</span>
                          </div>
                          <p className="text-sm font-medium text-gray-700">
                            {format(new Date(participation.run.startDate), 'MMM d')} - {format(new Date(participation.run.endDate), 'MMM d')}
                          </p>
                        </div>
                        <button
                          onClick={() => participation.winnersCount > 0 && setShowWinnersModal(participation)}
                          disabled={participation.winnersCount === 0}
                          className={`rounded-lg p-3 text-left w-full transition-colors ${
                            participation.winnersCount > 0
                              ? 'bg-amber-50 hover:bg-amber-100 cursor-pointer'
                              : 'bg-gray-100 cursor-default'
                          }`}
                        >
                          <div className={`flex items-center gap-2 mb-1 ${
                            participation.winnersCount > 0 ? 'text-amber-600' : 'text-gray-500'
                          }`}>
                            <Trophy className="w-4 h-4" />
                            <span className="text-xs font-medium">Winners</span>
                          </div>
                          {participation.winnersCount > 0 ? (
                            <p className="text-2xl font-bold text-amber-700">
                              {participation.winnersCount} <span className="text-sm font-normal">drawn</span>
                            </p>
                          ) : (
                            <p className="text-sm text-gray-600">
                              {participation.run.numberOfWinners || participation.run.campaign.numberOfWinners} to be drawn
                            </p>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Opt Out Warning Modal */}
      {showOptOutWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Opt Out of Campaign?</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Your QR code will be deactivated and customers will no longer be able to scan it for this campaign. You can opt back in at any time while the campaign is still active.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowOptOutWarning(false);
                  setPendingOptOut(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmOptOut}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Opt Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Winners Modal */}
      {showWinnersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Trophy className="w-6 h-6 text-amber-500" />
                  Campaign Winners
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {showWinnersModal.run.campaign.name} - Occurrence #{showWinnersModal.run.instanceNumber}
                </p>
              </div>
              <button
                onClick={() => setShowWinnersModal(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {showWinnersModal.winners.length > 0 ? (
                <div className="space-y-3">
                  {showWinnersModal.winners.map((winner, index) => (
                    <div
                      key={winner.id}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                        winner.prizeRank === 1 ? 'bg-amber-500' :
                        winner.prizeRank === 2 ? 'bg-gray-400' :
                        winner.prizeRank === 3 ? 'bg-amber-700' :
                        'bg-gray-300'
                      }`}>
                        {winner.prizeRank}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{winner.customerName}</p>
                        <p className="text-sm text-gray-500">From: {winner.storeName}</p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          winner.prizeRank === 1 ? 'bg-amber-100 text-amber-700' :
                          winner.prizeRank === 2 ? 'bg-gray-100 text-gray-700' :
                          winner.prizeRank === 3 ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {winner.prizeRank === 1 ? '1st Place' :
                           winner.prizeRank === 2 ? '2nd Place' :
                           winner.prizeRank === 3 ? '3rd Place' :
                           `${winner.prizeRank}th Place`}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No winners have been drawn yet</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Winners are drawn from all participating stores in this campaign
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
